import { useEffect, useMemo, useState } from 'react';
import type { components } from '@libertify/api-types/media';
import type {
  AudioTrack,
  QuickRecallCard,
  QuizAddonQuestion,
  StandoutItem,
} from '@libertify/ui';
import type {
  AddOnState,
  AddOnType,
  ExecutiveSummary,
  Flashcards,
  MediaAsset,
  MediaProjectResponse,
  Quiz,
  Standouts,
} from '../types';
import { fetchJsonAsset } from '../utils';
import { parseStandouts } from './parseStandouts';
import { parseQuickRecall } from './quickRecall';
import {
  assetUrl,
  projectUrl,
  readJson,
  resolvePresentAddons,
} from './addOnAssets';

export type { AddOnType };
type AudioDocContent = components['schemas']['AudioDocContent'];
type PodcastScript = components['schemas']['PodcastScript'];
export type AddOnStatus = components['schemas']['AddOnStatus'];

export interface UseExperienceAddonsArgs {
  /** Backend API base URL (admin: `API_URL`; sales: its public API URL). */
  apiBaseUrl: string;
  /** Owning project id (`publication.project_id`). */
  projectId: string;
  /**
   * Bearer for the authed project + asset reads (admin: session `accessToken`;
   * sales: the HttpOnly cookie resolved via a server action). Both arrive
   * asynchronously, so it's a value in the effect deps — the hook waits for it
   * and (re)fetches once it's present, rather than firing unauthenticated.
   */
  token?: string | null;
  /**
   * Bump to force a re-read of the add-on manifest + assets — e.g. after a
   * realtime "add-on Generated" event so a newly-produced asset is picked up.
   */
  refetchTrigger?: number;
  /**
   * The `refetchTrigger` whose read brings a regenerated episode. Until that
   * read ends, the episode loaded before it no longer counts as settled.
   */
  podcastReadAwaited?: number;
}

export interface QuizResult {
  /** Parsed questions for `<Quiz>` (empty when no quiz exists). */
  questions: QuizAddonQuestion[];
  error: boolean;
}

export interface SummaryResult {
  /** Markdown summary for `<Summary>` (empty string when none exists). */
  content: string;
  error: boolean;
}

/**
 * Where a content read that follows a `Generated` status stands:
 * - `loading`: the read is still running;
 * - `renewed`: it put new content in place;
 * - `done`: it ended without new content (kept the previous one, or found none).
 */
export type ContentRead = 'loading' | 'renewed' | 'done';

/**
 * Podcast and AudioDoc are independent Knowledge Hub tabs (MED-7075) — each
 * gets its own top-level result, same `{content, error}` shape as every
 * other add-on, so one failing never masks the other's working player.
 */
export interface PodcastResult {
  /** The playable episode (undefined until generated). */
  track?: AudioTrack;
  /** Fingerprint of the episode's mp3, which changes on regeneration. */
  audioHash?: string | null;
  /**
   * The episode read: `loading` during the first read and the one in
   * `podcastReadAwaited`; `renewed` once that awaited read pinned a new track.
   * A `Generated` manifest status precedes the track, so consumers wait on
   * this before showing the player and its "ready" toast.
   */
  read: ContentRead;
  error: boolean;
}

/** The podcast as held in state; `read` is derived at render. */
type PodcastState = Omit<PodcastResult, 'read'> & {
  /** The `refetchTrigger` of the read that pinned `track`. */
  trackReadFor?: number;
};

type AddonsState = Omit<ExperienceAddons, 'podcast'> & {
  podcast: PodcastState;
};

export interface AudioDocResult {
  /** The generated reading (undefined until generated). */
  track?: AudioTrack;
  error: boolean;
}

export interface QuickRecallResult {
  /** Parsed cards for `<QuickRecall>` (empty when no deck exists). */
  cards: QuickRecallCard[];
  error: boolean;
}

export interface StandoutsResult {
  standouts: StandoutItem[];
  error: boolean;
}

export interface ProjectInfo {
  /**
   * Owning organization, from the project itself — a publication-free source
   * for consumers whose instance may not exist (videoless projects 400 on the
   * instances endpoint, cf MED-4595).
   */
  organizationId: string;
  /** Project title, shown as the experience page header title. */
  title?: string;
  /** Project-level chat setting; `"Disabled"` means don't offer chat. */
  chatMode?: components['schemas']['ChatMode'] | null;
}

export interface ExperienceAddons {
  /** True while the single project read + asset fetches are in flight. */
  loading: boolean;
  /**
   * Add-on types the project manifest lists with an asset — the "ready"
   * signal for add-ons whose content documents are still published empty
   * (AudioDoc, cf MED-6934). Presence is NOT completion for Podcast, whose
   * primary asset is the script and exists from the start of generation —
   * read `addOnStatuses` for that one.
   */
  presentAddOns: AddOnType[];
  /**
   * Per-add-on generation status straight from the manifest — the only
   * completion signal for add-ons whose asset predates their content
   * (Podcast: its script asset is listed while the episode is still being
   * produced, so `presentAddOns` would call it ready far too early).
   */
  addOnStatuses: Partial<Record<AddOnType, AddOnStatus>>;
  quiz: QuizResult;
  summary: SummaryResult;
  podcast: PodcastResult;
  audioDoc: AudioDocResult;
  quickRecall: QuickRecallResult;
  standouts: StandoutsResult;
  /**
   * The project's own documents, from the same project read. User-provided at
   * creation so they exist immediately — a document source for consumers whose
   * publication/instance hasn't materialized its documents yet.
   */
  projectDocuments: MediaProjectResponse['documents'];
  /** Org + chat settings from the same project read; undefined until it lands. */
  projectInfo?: ProjectInfo;
  // Future add-ons (bookmarks, trivia, …): add a parser + a typed field here;
  // they're discovered and fetched automatically by the pass below.
}

/** The manifest's per-add-on status (first storyboard entry per type), for
 *  consumers that need completion rather than mere presence (see
 *  `addOnStatuses`). Scanned independently of `resolvePresentAddons`: an
 *  add-on that FAILED before producing any asset has no `asset_id`, so
 *  presence would drop it and its Error would read as "still generating". */
const resolveAddOnStatuses = (
  project: MediaProjectResponse,
): Partial<Record<AddOnType, AddOnStatus>> => {
  const byType: Partial<Record<AddOnType, AddOnStatus>> = {};
  for (const storyboard of project.storyboards ?? []) {
    for (const addOn of storyboard.add_ons ?? []) {
      byType[addOn.add_on_type] ??= addOn.status;
    }
  }
  return byType;
};

/** Map the raw quiz asset to the `<Quiz>` question shape (snake → camel). */
const parseQuiz = (quiz: Quiz | null): QuizAddonQuestion[] =>
  (quiz?.questions ?? []).map((q) => ({
    question: q.question,
    answers: (q.answers ?? []).map((a) => ({
      text: a.text,
      isCorrect: a.is_correct,
    })),
  }));

/** Build the `<Audio>` track from the podcast's mp3 asset, titled by its
 *  script. */
const buildPodcastTrack = (
  script: PodcastScript | null,
  audio: MediaAsset,
): AudioTrack => ({
  url: audio.path,
  title: script?.title || audio.description,
  // `MediaAsset.duration` is milliseconds; the track carries seconds.
  durationSeconds: audio.duration != null ? audio.duration / 1000 : undefined,
});

const EMPTY: AddonsState = {
  loading: false,
  presentAddOns: [],
  addOnStatuses: {},
  quiz: { questions: [], error: false },
  summary: { content: '', error: false },
  podcast: { error: false },
  audioDoc: { error: false },
  quickRecall: { cards: [], error: false },
  standouts: { standouts: [], error: false },
  projectDocuments: [],
};

/**
 * Keep the FIRST-seen project documents once published: re-reads
 * (`refetchTrigger` bumps when an add-on finishes) return re-signed paths for
 * the same unchanged files, and a changed url makes the PDF viewer reload the
 * document — a visible flash on every add-on completion.
 */
const keepFirstDocuments = (
  prev: AddonsState,
  fresh: MediaProjectResponse['documents'],
): MediaProjectResponse['documents'] =>
  prev.projectDocuments.length > 0 ? prev.projectDocuments : fresh;

/** `<TrackPlayer>` is keyed on the url, so a re-signed one would remount it
 *  and restart playback mid-listen: keep the pinned track while its audio is
 *  the same, and switch only once a regeneration changed the mp3's hash. */
type PinnedTrack = Pick<PodcastState, 'track' | 'audioHash' | 'trackReadFor'>;
const keepSameTrack = (prev: PinnedTrack, fresh: PinnedTrack): PinnedTrack =>
  prev.track &&
  (!fresh.track || !fresh.audioHash || fresh.audioHash === prev.audioHash)
    ? prev
    : fresh;

/** Whether any add-on content or document has already loaded — used to decide
 *  a re-read failure should preserve state rather than wipe it to errors.
 *  Manifest presence counts: a generated-but-empty add-on settles into its
 *  tab's empty state, and a transient failure must not flip that to an error. */
const hasLoadedContent = (state: AddonsState): boolean =>
  state.presentAddOns.length > 0 ||
  state.quiz.questions.length > 0 ||
  state.summary.content !== '' ||
  Boolean(state.podcast.track || state.audioDoc.track) ||
  state.quickRecall.cards.length > 0 ||
  state.standouts.standouts.length > 0 ||
  state.projectDocuments.length > 0;

/**
 * State updater for a failed project read: on the first load nothing has
 * rendered yet → surface the error; a `refetchTrigger` re-read runs after
 * content shows, so a transient failure keeps the settled state instead of
 * wiping it.
 */
const onReadFailure = (prev: AddonsState): AddonsState =>
  hasLoadedContent(prev)
    ? { ...prev, loading: false }
    : {
        loading: false,
        presentAddOns: [],
        addOnStatuses: {},
        quiz: { questions: [], error: true },
        summary: { content: '', error: true },
        podcast: { error: true },
        audioDoc: { error: true },
        quickRecall: { cards: [], error: true },
        standouts: { standouts: [], error: true },
        projectDocuments: [],
      };

/**
 * Client-side loader for a project's Knowledge Hub add-on assets. The asset
 * URLs aren't on the publication response, so this runs the read chain the
 * backend exposes:
 *
 *   GET /projects/{id}            → storyboards[].add_ons[] (the manifest)
 *   GET /media/assets/{assetId}   → MediaAsset.path (signed URL)
 *   GET {path}                    → the add-on JSON
 *
 * Podcast adds a 4th step: the manifest names a companion audio asset
 * (`secondary_asset_id`), read for the playable path, while its primary asset
 * holds the script (see loadPodcastTrack). AudioDoc's JSON is still empty, so
 * it never yields a track.
 *
 * The project is read **once**; that response lists which add-ons exist, so we
 * fetch each present asset a single time, in parallel, failing independently (a
 * missing/broken quiz never affects the summary). Shared by admin + sales (no
 * Refine); each supplies its own `apiBaseUrl` + `token`. Steps 1–2 are authed;
 * the signed `path` is not.
 */
export function useExperienceAddons({
  apiBaseUrl,
  projectId,
  token,
  refetchTrigger,
  podcastReadAwaited,
}: UseExperienceAddonsArgs): ExperienceAddons {
  const [addons, setAddons] = useState<AddonsState>(() => ({
    ...EMPTY,
    loading: Boolean(apiBaseUrl && projectId),
  }));
  // The `refetchTrigger` whose read last finished; null before the first one.
  const [readDoneFor, setReadDoneFor] = useState<number | null>(null);

  useEffect(() => {
    // Wait for the bearer — the tokens arrive async, and firing without one
    // would 401 with no retry (the effect only re-runs when a dep changes).
    // NOTE: state is NOT reset when `projectId` changes — every route into this
    // page currently remounts, so a param-only project switch can't happen. If
    // a project-switcher link is ever added, key the consumer by `projectId`
    // (else the pinned documents and podcast track below would leak across
    // projects).
    if (!apiBaseUrl || !projectId || !token) return;
    let cancelled = false;
    const trigger = refetchTrigger ?? 0;

    const authedJson = <T>(url: string) => readJson<T>(url, token);

    const fetchAssetMeta = (assetId: string) =>
      authedJson<MediaAsset>(assetUrl(apiBaseUrl, assetId));

    // Steps 2–3 for one add-on: asset metadata → signed path → JSON.
    const fetchAsset = async <T>(assetId: string): Promise<T | null> =>
      fetchJsonAsset<T>((await fetchAssetMeta(assetId)).path);

    void (async () => {
      let present: Map<AddOnType, AddOnState>;
      let addOnStatuses: Partial<Record<AddOnType, AddOnStatus>>;
      let projectDocuments: MediaProjectResponse['documents'] = [];
      let projectInfo: ProjectInfo | undefined;
      try {
        // 1. project → the add-on manifest (read ONCE for every add-on).
        const project = await authedJson<MediaProjectResponse>(
          projectUrl(apiBaseUrl, projectId),
        );
        present = resolvePresentAddons(project);
        addOnStatuses = resolveAddOnStatuses(project);
        projectDocuments = project.documents ?? [];
        projectInfo = {
          organizationId: project.organization_id,
          title: project.title,
          chatMode: project.settings?.chat_mode,
        };
        // Documents are user-provided and needed immediately — publish them
        // (and the manifest presence) as soon as the project read lands,
        // without waiting for the add-on asset fetches below (first-seen
        // copy wins; see keepFirstDocuments).
        if (!cancelled) {
          setAddons((prev) => ({
            ...prev,
            presentAddOns: [...present.keys()],
            addOnStatuses,
            projectDocuments: keepFirstDocuments(prev, projectDocuments),
            projectInfo,
          }));
        }
      } catch (err) {
        // The read failed → surface an error, or preserve settled state on a
        // transient re-read failure (see onReadFailure).
        console.warn('[experience] add-ons project read failed', err);
        if (!cancelled) {
          setAddons(onReadFailure);
          setReadDoneFor(trigger);
        }
        return;
      }

      // 2–3. Fetch the raw JSON for each present add-on in parallel; each one
      // fails on its own (absent add-on → skipped, stays empty & not-error).
      const load = <T>(type: AddOnType) => {
        const assetId = present.get(type)?.asset_id;
        if (!assetId)
          return Promise.resolve({ data: null as T | null, error: false });
        return fetchAsset<T>(assetId)
          .then((data) => ({ data, error: false }))
          .catch((err) => {
            console.warn(`[experience] ${type} load failed`, err);
            return { data: null as T | null, error: true };
          });
      };

      // Podcast: the manifest names two assets — the script (primary) and the
      // mp3 (`secondary_asset_id`). Kept inside the Promise.all so it runs
      // alongside the others, failing alone.
      const loadPodcastTrack = async (): Promise<{
        data: AudioTrack | null;
        audioHash?: string | null;
        error: boolean;
      }> => {
        const podcast = present.get('Podcast');
        if (!podcast?.asset_id) {
          // Finished with no asset at all is a failure, not an empty tab.
          const status = addOnStatuses.Podcast;
          return {
            data: null,
            error: status === 'Generated' || status === 'OutOfDate',
          };
        }
        const audioAssetId = podcast.secondary_asset_id;
        if (!audioAssetId) {
          // Still generating → empty tab. Generated with no episode is a real
          // failure and must show as one (MED-7042) — and so is a generation
          // that ended in Error, which would otherwise read as "in progress".
          // OutOfDate counts as Generated: stale, but finished.
          const isFinished =
            podcast.status === 'Generated' || podcast.status === 'OutOfDate';
          const failed = isFinished || podcast.status === 'Error';
          if (isFinished) {
            console.warn(
              '[experience] Podcast generated without a secondary_asset_id',
            );
          }
          return { data: null, error: failed };
        }
        try {
          // The mp3 is what makes the track playable, so only its read is
          // fatal — a failed script read just costs the episode title.
          const [script, audio] = await Promise.all([
            fetchAsset<PodcastScript>(podcast.asset_id).catch((err) => {
              console.warn('[experience] Podcast script load failed', err);
              return null;
            }),
            fetchAssetMeta(audioAssetId),
          ]);
          return {
            data: buildPodcastTrack(script, audio),
            audioHash: script?.secondary_asset_hash,
            error: false,
          };
        } catch (err) {
          console.warn('[experience] Podcast load failed', err);
          return { data: null, error: true };
        }
      };

      const [quiz, summary, audioDoc, podcast, quickRecall, standouts] =
        await Promise.all([
          load<Quiz>('Quiz'),
          load<ExecutiveSummary>('Summary'),
          load<AudioDocContent>('AudioDoc'),
          loadPodcastTrack(),
          load<Flashcards>('Flashcards'),
          load<Standouts>('Standouts'),
        ]);
      if (cancelled) return;

      setAddons((prev) => {
        // Resolved before the error below: a pinned track is still playable,
        // so a failed re-read must not turn the tab red under it.
        const podcastTrack = keepSameTrack(prev.podcast, {
          track: podcast.data ?? undefined,
          audioHash: podcast.audioHash,
          trackReadFor: podcast.data ? trigger : undefined,
        });
        return {
          loading: false,
          presentAddOns: [...present.keys()],
          addOnStatuses,
          quiz: { questions: parseQuiz(quiz.data), error: quiz.error },
          summary: {
            content: summary.data?.content ?? '',
            error: summary.error,
          },
          // Each tab errors independently — a failed read only masks ITS OWN
          // tab when that tab has nothing playable either.
          podcast: {
            ...podcastTrack,
            error: !podcastTrack.track && podcast.error,
          },
          audioDoc: {
            track: undefined, // TODO: AudioDocContent is still {}
            error: audioDoc.error,
          },
          quickRecall: {
            cards: parseQuickRecall(quickRecall.data),
            error: quickRecall.error,
          },
          standouts: {
            standouts: parseStandouts(standouts.data),
            error: standouts.error,
          },
          projectDocuments: keepFirstDocuments(prev, projectDocuments),
          projectInfo,
        };
      });
      setReadDoneFor(trigger);
    })();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, projectId, token, refetchTrigger]);

  // Derived in render, so the awaited read counts as loading from the very
  // render that asks for it. Renewed only if that read pinned the track: a
  // failed read keeps the previous episode, which is not a new one.
  const { trackReadFor } = addons.podcast;
  let podcastRead: ContentRead = 'done';
  if (readDoneFor === null || readDoneFor < (podcastReadAwaited ?? 0))
    podcastRead = 'loading';
  else if (
    podcastReadAwaited !== undefined &&
    trackReadFor !== undefined &&
    trackReadFor >= podcastReadAwaited
  )
    podcastRead = 'renewed';
  return useMemo(() => {
    const { track, audioHash, error } = addons.podcast;
    return { ...addons, podcast: { track, audioHash, error, read: podcastRead } };
  }, [addons, podcastRead]);
}
